import { Card } from '@pericles/framework-frontend';
import { useEffect, useMemo, useState } from 'react';

import {
  fetchRuntimeCockpitTelemetry,
  type RuntimeCockpitRuntimeTest,
  type RuntimeCockpitTelemetryPayload,
} from '../api/runtimeCockpitTelemetryClient';

import { Button } from './ui';

function statusTone(status: string): string {
  if (status === 'approved') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (status === 'longwalls_verified_done')
    return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (status === 'connected') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (status === 'running') return 'border-blue-200 bg-blue-50 text-blue-700';
  if (status === 'telemetry_pending') return 'border-blue-200 bg-blue-50 text-blue-700';
  if (status === 'stale') return 'border-amber-200 bg-amber-50 text-amber-700';
  if (status === 'codex_claimed_done') return 'border-amber-200 bg-amber-50 text-amber-700';
  if (
    status === 'blocked' ||
    status === 'failed' ||
    status === 'disconnected' ||
    status === 'invalid_signature' ||
    status === 'blocked_by_policy' ||
    status === 'validation_failed'
  ) {
    return 'border-red-200 bg-red-50 text-red-700';
  }
  return 'border-slate-200 bg-slate-50 text-slate-700';
}

function formatNumber(value: number | null | undefined, suffix = ''): string {
  if (value === null || value === undefined) return 'n/a';
  return `${Number(value.toFixed(2))}${suffix}`;
}

function progressPercent(startIso?: string | null, endIso?: string | null): number {
  if (!startIso || !endIso) return 0;
  const start = Date.parse(startIso);
  const end = Date.parse(endIso);
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return 0;
  return Math.max(0, Math.min(100, Math.round(((Date.now() - start) / (end - start)) * 100)));
}

function formatAge(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return 'n/a';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

function formatDurationHours(hours: number | null | undefined): string {
  if (hours === null || hours === undefined) return 'n/a';
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  return `${hours.toFixed(2)}h`;
}

function formatFastMode(value: boolean | null | undefined): string {
  return value ? 'yes' : 'no';
}

function codexDepthVariantRank(id: string): number {
  const match = id.toLowerCase().match(/test2([a-z])?/);
  if (!match) return 0;
  const suffix = match[1];
  if (!suffix) return 10;
  return (suffix.charCodeAt(0) - 'a'.charCodeAt(0) + 1) * 10;
}

function codexDepthRuntimeScore(test: RuntimeCockpitRuntimeTest): number {
  const isReadiness = test.id.includes('readiness');
  const isRunning = test.status === 'running';
  const isLive = test.liveLaneCount > 0;
  return (
    codexDepthVariantRank(test.id) * 100 +
    (isRunning ? 1000 : 0) +
    (isLive ? 200 : 0) +
    (isReadiness ? 0 : 50)
  );
}

function pickPrimaryCodexDepthTest(
  runtimeTests: RuntimeCockpitRuntimeTest[],
): RuntimeCockpitRuntimeTest | null {
  const depthTests = runtimeTests.filter((item) => item.family === 'codex_depth_generation');
  return (
    [...depthTests].sort(
      (left, right) => codexDepthRuntimeScore(right) - codexDepthRuntimeScore(left),
    )[0] ?? null
  );
}

function formatViewport(
  viewport:
    | string
    | {
        width?: number | string | null;
        height?: number | string | null;
      }
    | null
    | undefined,
): string {
  if (!viewport) return 'viewport n/a';
  if (typeof viewport === 'string') return viewport;
  const width = viewport.width ?? null;
  const height = viewport.height ?? null;
  if (width !== null && height !== null) return `${width}x${height}`;
  if (width !== null) return `${width}px wide`;
  if (height !== null) return `${height}px high`;
  return 'viewport object';
}

function bridgeHeadline(status: string | undefined): string {
  if (status === 'connected') return 'Cloud snapshot live';
  if (status === 'stale') return 'Cloud snapshot stale';
  if (status === 'blocked_external_dependency') return 'Live cloud dependency blocked';
  if (status === 'invalid_signature') return 'Snapshot signature rejected';
  if (status === 'blocked_by_policy') return 'Snapshot blocked by policy';
  if (status === 'validation_failed') return 'Snapshot validation failed';
  return 'Local bridge disconnected';
}

function statusLabel(status: string): string {
  if (status === 'longwalls_verified_done') return 'approved';
  if (status === 'telemetry_pending') return 'pending';
  return status.replace(/_/g, ' ');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function metricRows(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function formatMetricValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'n/a';
  if (typeof value === 'number') return formatNumber(value);
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  return String(value).replace(/_/g, ' ');
}

function GateMetricDetails({
  details,
}: {
  details?: Record<string, unknown> | null;
}): JSX.Element | null {
  if (!details) return null;
  const domainRuntimeMetrics = metricRows(details.domainRuntimeMetrics);
  const tokenAccountingMetrics = metricRows(details.tokenAccountingMetrics);
  const scoreItems = [
    { label: 'attempt', value: details.selectedAttemptId },
    { label: 'replay', value: details.replayStatus },
    { label: 'live score', value: details.actualLiveEvidenceScore },
    {
      label: 'readiness',
      value: details.currentAttemptEngineReadinessScore ?? details.preNextRunEngineReadinessScore,
    },
    { label: 'evolution', value: details.evolutionIndex },
    { label: 'undercount', value: details.statusUndercountTotalTokens },
    { label: 'S8', value: details.s8Verdict },
  ].filter((item) => item.value !== undefined && item.value !== null);

  if (!domainRuntimeMetrics.length && !tokenAccountingMetrics.length && !scoreItems.length) {
    return null;
  }

  return (
    <div className="mt-3 space-y-3 border-t border-slate-200 pt-3 text-xs text-slate-600">
      {scoreItems.length ? (
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {scoreItems.map((item) => (
            <span
              key={item.label}
              className="rounded border border-slate-200 bg-slate-50 px-2 py-1"
            >
              {item.label}{' '}
              <strong className="text-slate-900">{formatMetricValue(item.value)}</strong>
            </span>
          ))}
        </div>
      ) : null}
      {domainRuntimeMetrics.length ? (
        <div>
          <div className="font-semibold uppercase tracking-wide text-slate-500">
            Domain runtime metrics
          </div>
          <div className="mt-2 grid gap-2 md:grid-cols-2">
            {domainRuntimeMetrics.map((row) => (
              <div
                key={String(row.domain ?? row.runId)}
                className="rounded-lg border border-slate-200 bg-slate-50 p-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <strong className="text-slate-900">{formatMetricValue(row.domain)}</strong>
                  <span>{formatMetricValue(row.status)}</span>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-1">
                  <span>
                    steps {formatMetricValue(row.observedStepCount)}/
                    {formatMetricValue(row.requiredStepCount)}
                  </span>
                  <span>
                    roles {formatMetricValue(row.observedRoleCount)}/
                    {formatMetricValue(row.requiredRoleCount)}
                  </span>
                  <span>mutations {formatMetricValue(row.persistedMutationCount)}</span>
                  <span>closure {formatMetricValue(row.closureTraceCount)}</span>
                  <span>
                    shots {formatMetricValue(row.uniqueScreenshotHashCount)}/
                    {formatMetricValue(row.screenshotCount)}
                  </span>
                  <span>KPI {formatMetricValue(row.kpiChanged)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {tokenAccountingMetrics.length ? (
        <div>
          <div className="font-semibold uppercase tracking-wide text-slate-500">
            Token accounting metrics
          </div>
          <div className="mt-2 grid gap-2 md:grid-cols-2">
            {tokenAccountingMetrics.map((row) => (
              <div
                key={String(row.runId ?? row.domain)}
                className="rounded-lg border border-slate-200 bg-slate-50 p-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <strong className="text-slate-900">{formatMetricValue(row.domain)}</strong>
                  <span>entries {formatMetricValue(row.entryCount)}</span>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-1">
                  <span>status {formatMetricValue(row.statusObservedTotalTokens)}</span>
                  <span>raw {formatMetricValue(row.rawEntryTotalTokens)}</span>
                  <span>delta {formatMetricValue(row.deltaTotalTokens)}</span>
                  <span>undercount {formatMetricValue(row.statusUndercountTotalTokens)}</span>
                  <span>duplicates {formatMetricValue(row.duplicateExecutionCount)}</span>
                  <span>reset {formatMetricValue(row.resetDetected)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function fallbackRuntimeTests(
  telemetry: RuntimeCockpitTelemetryPayload | null,
): RuntimeCockpitRuntimeTest[] {
  if (!telemetry) return [];
  const a75 = telemetry.program.items.find((item) => item.id === 'a75-48h-runtime-observation');
  const test2 = telemetry.program.items.find((item) => item.id === 'test2-codex-depth');
  return [
    {
      id: 'a75-48h-runtime-observation',
      title: a75?.title ?? 'A75 48h runtime observation',
      description: 'Legacy cockpit view derived from aggregate lanes.',
      family: 'runtime_endurance',
      status: telemetry.aggregate.summary.liveLaneCount >= 12 ? 'running' : 'blocked',
      stage: a75?.lockReason ?? 'runtime telemetry loaded',
      startedAt: a75?.plannedStartAt ?? null,
      targetEndAt: a75?.plannedEndAt ?? null,
      progressPercent: progressPercent(a75?.plannedStartAt, a75?.plannedEndAt),
      laneCount: a75?.targetConcurrency ?? telemetry.aggregate.summary.laneCount,
      liveLaneCount: telemetry.aggregate.summary.liveLaneCount,
      budget: {
        status: telemetry.aggregate.budget.status,
        inputTokens: telemetry.aggregate.budget.inputTokens,
        outputTokens: telemetry.aggregate.budget.outputTokens,
        totalTokens: telemetry.aggregate.budget.totalTokens,
        egressMb: telemetry.aggregate.budget.egressMb,
        egressBudgetMb: telemetry.aggregate.budget.egressBudgetMb,
      },
      gates: [
        {
          id: 'aggregate_loaded',
          label: 'Aggregate loaded',
          status: telemetry.aggregate.status === 'failed' ? 'failed' : 'running',
          evidenceRefs: telemetry.aggregate.evidenceRefs ?? [],
        },
      ],
      lanes: telemetry.aggregate.lanes.slice(0, 12).map((lane) => ({
        id: lane.laneId,
        label: lane.laneId,
        status: lane.status === 'failed' || lane.processAlive === false ? 'failed' : 'running',
        heartbeatCount: lane.heartbeatCount,
        checkpointCount: lane.checkpointCount,
        processAlive: lane.processAlive,
        evidenceRefs: [],
      })),
      evidenceRefs: telemetry.aggregate.evidenceRefs ?? [],
      claimBoundary: 'Fallback test view; full runtimeTests matrix is missing from this snapshot.',
    },
    {
      id: 'test2-codex-depth-4h',
      title: test2?.title ?? 'Test 2 Codex depth lanes',
      description: 'Legacy readiness view; no launched lane telemetry in this snapshot.',
      family: 'codex_depth_generation',
      status:
        test2?.status === 'blocked'
          ? 'blocked'
          : telemetry.aggregate.summary.test2Locked
            ? 'blocked'
            : 'planned',
      stage: test2?.lockReason ?? telemetry.aggregate.summary.test2LockReason,
      startedAt: test2?.plannedStartAt ?? null,
      targetEndAt: test2?.plannedEndAt ?? null,
      progressPercent: 0,
      laneCount: test2?.targetConcurrency ?? 2,
      liveLaneCount: 0,
      budget: {
        status: 'telemetry_pending',
        inputTokens: null,
        outputTokens: null,
        totalTokens: null,
        egressMb: 0,
        egressBudgetMb: null,
      },
      gates: [
        {
          id: 'launch_lock',
          label: 'Launch lock',
          status: telemetry.aggregate.summary.test2Locked ? 'blocked' : 'approved',
          evidenceRefs: [],
        },
      ],
      lanes: [],
      evidenceRefs: test2?.evidenceRefs ?? [],
      claimBoundary: 'Test 2 cannot be scored without runtimeTests lane telemetry.',
    },
  ];
}

export function RuntimeCockpitPanel({
  telemetry,
  isLoading = false,
  error = null,
  onRefresh,
}: {
  telemetry: RuntimeCockpitTelemetryPayload | null;
  isLoading?: boolean;
  error?: string | null;
  onRefresh?: () => void;
}): JSX.Element {
  const a75 = telemetry?.program.items.find((item) => item.id === 'a75-48h-runtime-observation');
  const test2 = telemetry?.program.items.find((item) => item.id === 'test2-codex-depth');
  const progress = progressPercent(a75?.plannedStartAt, a75?.plannedEndAt);
  const visibleLanes = telemetry?.aggregate.lanes.slice(0, 12) ?? [];
  const claimedLayers =
    telemetry?.verifiedDone.layers.filter((layer) => layer.claimedByCodex) ?? [];
  const verifiedLayers =
    telemetry?.verifiedDone.layers.filter((layer) => layer.verifiedByLongwalls) ?? [];
  const bridge = telemetry?.cloudBridge;
  const bridgeStatus = bridge?.status ?? (telemetry ? 'disconnected' : 'not_loaded');
  const runtimeTests = telemetry?.report.runtimeTests?.length
    ? telemetry.report.runtimeTests
    : fallbackRuntimeTests(telemetry);
  const [selectedTestId, setSelectedTestId] = useState<string | null>(null);
  const test2Test = pickPrimaryCodexDepthTest(runtimeTests);
  const preferredSelectedTestId = test2Test?.id ?? runtimeTests[0]?.id ?? null;
  const selectedTest =
    runtimeTests.find((item) => item.id === selectedTestId) ??
    runtimeTests.find((item) => item.id === preferredSelectedTestId) ??
    runtimeTests[0] ??
    null;
  const a75Test = runtimeTests.find((item) => item.id === 'a75-48h-runtime-observation');
  const visibleA75Lanes =
    a75Test?.lanes.slice(0, 12).map((lane) => ({
      id: lane.id,
      label: lane.label,
      status: lane.status,
      heartbeatCount: lane.heartbeatCount,
      checkpointCount: lane.checkpointCount,
      processAlive: lane.processAlive,
    })) ??
    visibleLanes.map((lane) => ({
      id: lane.laneId,
      label: lane.laneId,
      status: lane.status,
      heartbeatCount: lane.heartbeatCount,
      checkpointCount: lane.checkpointCount,
      processAlive: lane.processAlive,
    }));
  const test2Headline =
    test2Test?.status === 'running'
      ? 'Test 2 Codex is running'
      : test2Test?.status === 'approved'
        ? 'Test 2 approved'
        : test2Test?.status === 'codex_claimed_done'
          ? 'Test 2 Codex completed, verification pending'
          : test2Test?.status === 'failed'
            ? 'Test 2 failed'
            : telemetry?.aggregate.summary.test2Locked
              ? 'Test 2 Codex remains locked'
              : 'Test 2 Codex is unlocked';
  const selectedVisualEvidence = selectedTest
    ? [
        ...(selectedTest.visualEvidence ?? []),
        ...selectedTest.lanes.flatMap((lane) => lane.visualEvidence ?? []),
      ]
    : [];
  const selectedCodexProfile = selectedTest?.codexExecutionProfile ?? null;

  useEffect(() => {
    if (!runtimeTests.length) {
      if (selectedTestId !== null) setSelectedTestId(null);
      return;
    }
    if (!selectedTestId || !runtimeTests.some((item) => item.id === selectedTestId)) {
      setSelectedTestId(preferredSelectedTestId);
    }
  }, [runtimeTests, selectedTestId, preferredSelectedTestId]);

  return (
    <section className="space-y-5" data-testid="runtime-cockpit-panel">
      <Card
        className="overflow-hidden border-slate-200 bg-white shadow-sm"
        bodyClassName="space-y-5"
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
              LongWalls Runtime Cockpit
            </div>
            <h2 className="mt-2 text-2xl font-semibold text-slate-950">
              Cloud-visible runtime control room
            </h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
              The local bridge pushes signed, redacted, low-egress snapshots to the cloud surface.
              Vercel never reads local files directly, and Codex claimed done remains separate from
              LongWalls verified done.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <span
              className={`rounded-full border px-3 py-1 text-xs font-semibold ${statusTone(telemetry?.aggregate.status ?? 'planned')}`}
            >
              {isLoading ? 'loading' : (telemetry?.aggregate.status ?? 'not loaded')}
            </span>
            <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
              {telemetry?.mode ?? 'file_backed_dev'}
            </span>
            <span
              className={`rounded-full border px-3 py-1 text-xs font-semibold ${statusTone(bridgeStatus)}`}
            >
              {bridgeStatus}
            </span>
            {onRefresh ? (
              <Button variant="secondary" size="sm" onClick={onRefresh}>
                Refresh
              </Button>
            ) : null}
          </div>
        </div>

        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div
          className={`rounded-2xl border p-4 ${statusTone(bridgeStatus)} bg-opacity-70`}
          data-testid="runtime-cockpit-cloud-bridge"
        >
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide">
                Local to cloud bridge
              </div>
              <div className="mt-1 text-lg font-semibold">{bridgeHeadline(bridgeStatus)}</div>
              <div className="mt-1 text-xs">
                Last sync {bridge?.lastSyncAt ?? 'none'} - age {formatAge(bridge?.ageSeconds)} -
                sequence {bridge?.sequence ?? 'n/a'}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
              <span className="rounded-lg border border-white/60 bg-white/50 px-3 py-2">
                signed: {bridge?.signatureVerified ? 'yes' : 'no'}
              </span>
              <span className="rounded-lg border border-white/60 bg-white/50 px-3 py-2">
                redacted: {bridge?.redacted ? 'yes' : 'no'}
              </span>
              <span className="rounded-lg border border-white/60 bg-white/50 px-3 py-2">
                egress {formatNumber(bridge?.egressEstimateMb, ' MB')}
              </span>
              <span className="rounded-lg border border-white/60 bg-white/50 px-3 py-2">
                replay: {bridge?.replaySafe ? 'safe' : 'blocked'}
              </span>
            </div>
          </div>
          {bridge?.blockers.length ? (
            <div className="mt-3 rounded-lg border border-white/60 bg-white/50 p-3 text-xs">
              {bridge.blockers.join(' | ')}
            </div>
          ) : null}
        </div>

        <div className="grid gap-3 md:grid-cols-5">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              A75 lanes
            </div>
            <div
              className="mt-2 text-3xl font-semibold text-slate-950"
              data-testid="runtime-cockpit-live-lanes"
            >
              {a75Test?.liveLaneCount ?? telemetry?.aggregate.summary.liveLaneCount ?? 0}/
              {a75Test?.laneCount ?? a75?.targetConcurrency ?? 12}
            </div>
            <div className="mt-1 text-xs text-slate-500">
              A75 must stay at 12/12 without unsafe egress.
            </div>
          </div>
          <div
            className={`rounded-xl border p-4 ${statusTone(test2Test?.status ?? 'planned')}`}
            data-testid="runtime-cockpit-codex-depth-lanes"
          >
            <div className="text-xs font-semibold uppercase tracking-wide">Codex lanes</div>
            <div className="mt-2 text-3xl font-semibold">
              {test2Test?.liveLaneCount ?? 0}/{test2Test?.laneCount ?? 2}
            </div>
            <div className="mt-1 text-xs">
              {test2Test?.id
                ? `${test2Test.title} - ${statusLabel(test2Test.status)}`
                : 'No Test 2 lane telemetry.'}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              A75 progress
            </div>
            <div className="mt-2 h-2 rounded-full bg-slate-200">
              <div
                className="h-2 rounded-full bg-blue-600"
                style={{ width: `${a75Test?.progressPercent ?? progress}%` }}
              />
            </div>
            <div className="mt-2 text-sm font-semibold text-slate-900">
              {a75Test?.progressPercent ?? progress}% of 48h target
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Budget
            </div>
            <div className="mt-2 text-sm font-semibold text-slate-900">
              {telemetry?.aggregate.budget.status ?? 'unknown'}
            </div>
            <div className="mt-1 text-xs text-slate-500" data-testid="runtime-cockpit-budget-usage">
              {formatNumber(telemetry?.aggregate.budget.totalTokens)} tokens -{' '}
              {formatNumber(telemetry?.aggregate.budget.egressMb, ' MB')} egress
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Verified done
            </div>
            <div
              className="mt-2 text-sm font-semibold text-slate-900"
              data-testid="runtime-cockpit-verified-done"
            >
              {verifiedLayers.length} verified - {claimedLayers.length} Codex claimed
            </div>
            <div className="mt-1 text-xs text-slate-500">
              Claims cannot close without LongWalls proof.
            </div>
          </div>
        </div>

        <div
          className={`rounded-xl border p-4 text-sm ${statusTone(test2Test?.status ?? (telemetry?.aggregate.summary.test2Locked ? 'blocked' : 'planned'))}`}
          data-testid="runtime-cockpit-test2-lock"
        >
          <strong>{test2Headline}:</strong>{' '}
          {test2Test?.stage ??
            test2?.lockReason ??
            telemetry?.aggregate.summary.test2LockReason ??
            'Waiting for cockpit telemetry.'}
        </div>
      </Card>

      <Card className="border-slate-200 bg-white shadow-sm" bodyClassName="space-y-5">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Runtime test program
            </div>
            <h3 className="text-xl font-semibold text-slate-950">
              Navigable tests and execution states
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              Each test keeps its own `running`, `approved`, `failed` or `blocked` status. A75
              remains an endurance proof; Test 2 tracks the real Codex lanes.
            </p>
          </div>
          <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
            {runtimeTests.length} tests tracked
          </span>
        </div>

        <div className="grid gap-3 md:grid-cols-2" data-testid="runtime-cockpit-test-list">
          {runtimeTests.map((runtimeTest) => (
            <button
              key={runtimeTest.id}
              type="button"
              onClick={() => setSelectedTestId(runtimeTest.id)}
              className={`rounded-2xl border p-4 text-left transition ${
                selectedTest?.id === runtimeTest.id
                  ? 'border-blue-300 bg-blue-50 shadow-sm'
                  : 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-white'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    {runtimeTest.family}
                  </div>
                  <div className="mt-1 text-base font-semibold text-slate-950">
                    {runtimeTest.title}
                  </div>
                  <div className="mt-1 text-xs leading-5 text-slate-600">{runtimeTest.stage}</div>
                </div>
                <span
                  className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${statusTone(runtimeTest.status)}`}
                >
                  {statusLabel(runtimeTest.status)}
                </span>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-xs text-slate-600">
                <span className="rounded-lg border border-white bg-white/70 px-2 py-2">
                  lanes{' '}
                  <strong className="text-slate-950">
                    {runtimeTest.liveLaneCount}/{runtimeTest.laneCount}
                  </strong>
                </span>
                <span className="rounded-lg border border-white bg-white/70 px-2 py-2">
                  progress{' '}
                  <strong className="text-slate-950">{runtimeTest.progressPercent}%</strong>
                </span>
                <span className="rounded-lg border border-white bg-white/70 px-2 py-2">
                  budget{' '}
                  <strong className="text-slate-950">
                    {statusLabel(runtimeTest.budget.status)}
                  </strong>
                </span>
              </div>
              <div className="mt-3 h-2 rounded-full bg-slate-200">
                <div
                  className={`h-2 rounded-full ${runtimeTest.status === 'failed' ? 'bg-red-500' : runtimeTest.status === 'approved' ? 'bg-emerald-500' : runtimeTest.status === 'codex_claimed_done' ? 'bg-amber-500' : 'bg-blue-600'}`}
                  style={{ width: `${Math.max(2, runtimeTest.progressPercent)}%` }}
                />
              </div>
            </button>
          ))}
        </div>

        {selectedTest ? (
          <div
            className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
            data-testid="runtime-cockpit-selected-test"
          >
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className="text-lg font-semibold text-slate-950">{selectedTest.title}</h4>
                  <span
                    className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${statusTone(selectedTest.status)}`}
                  >
                    {statusLabel(selectedTest.status)}
                  </span>
                </div>
                <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-600">
                  {selectedTest.description}
                </p>
                <p className="mt-2 text-xs text-slate-500">{selectedTest.claimBoundary}</p>
              </div>
              <div className="grid min-w-[260px] grid-cols-2 gap-2 text-xs">
                <span className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  tokens <strong>{formatNumber(selectedTest.budget.totalTokens)}</strong>
                </span>
                <span className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  egress <strong>{formatNumber(selectedTest.budget.egressMb, ' MB')}</strong>
                </span>
                <span className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  started{' '}
                  <strong>
                    {selectedTest.startedAt
                      ? new Date(selectedTest.startedAt).toLocaleTimeString()
                      : 'n/a'}
                  </strong>
                </span>
                <span className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                  target{' '}
                  <strong>
                    {selectedTest.targetEndAt
                      ? new Date(selectedTest.targetEndAt).toLocaleTimeString()
                      : 'n/a'}
                  </strong>
                </span>
              </div>
            </div>

            <div
              className="mt-4 rounded-2xl border border-slate-200 bg-white p-4"
              data-testid="runtime-cockpit-codex-profile"
            >
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Codex execution profile
                  </div>
                  <div className="mt-1 text-base font-semibold text-slate-950">
                    {selectedCodexProfile?.modelDisplayName ??
                      selectedCodexProfile?.model ??
                      'Model not declared'}
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    {selectedCodexProfile?.claimBoundary ??
                      'The model profile is telemetry only; quality still depends on gates.'}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
                  <span className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    provider <strong>{selectedCodexProfile?.provider ?? 'n/a'}</strong>
                  </span>
                  <span className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    Reasoning <strong>{selectedCodexProfile?.reasoningEffort ?? 'n/a'}</strong>
                  </span>
                  <span className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    speed <strong>{selectedCodexProfile?.speedMode ?? 'n/a'}</strong>
                  </span>
                  <span
                    className={`rounded-lg border px-3 py-2 ${
                      selectedCodexProfile?.fastMode
                        ? 'border-red-200 bg-red-50 text-red-700'
                        : 'border-emerald-200 bg-emerald-50 text-emerald-700'
                    }`}
                  >
                    Fast mode <strong>{formatFastMode(selectedCodexProfile?.fastMode)}</strong>
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-[0.85fr_1.15fr]">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Gates
                </div>
                <div className="mt-2 space-y-2">
                  {selectedTest.gates.map((gate) => (
                    <div
                      key={gate.id}
                      className="rounded-lg border border-slate-200 bg-white p-3 text-sm"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-medium text-slate-900">{gate.label}</span>
                        <span
                          className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statusTone(gate.status)}`}
                        >
                          {statusLabel(gate.status)}
                        </span>
                      </div>
                      <GateMetricDetails details={gate.details} />
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between gap-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Lanes
                  </div>
                  <span className="text-xs font-semibold text-slate-500">
                    {selectedTest.lanes.length} lanes
                  </span>
                </div>
                <div className="mt-2 grid gap-2 md:grid-cols-2">
                  <div className="sr-only" data-testid="runtime-cockpit-test-lanes">
                    {selectedTest.lanes.map((lane) => `${lane.id}:${lane.status}`).join('|')}
                  </div>
                  {selectedTest.lanes.map((lane) => (
                    <div key={lane.id} className="rounded-lg border border-slate-200 bg-white p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="text-sm font-semibold text-slate-950">{lane.label}</div>
                          <div className="mt-1 text-xs text-slate-500">
                            {lane.runStatus ??
                              `heartbeat ${lane.heartbeatCount} - checkpoint ${lane.checkpointCount}`}
                          </div>
                        </div>
                        <span
                          className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statusTone(lane.status)}`}
                        >
                          {statusLabel(lane.status)}
                        </span>
                      </div>
                      <div className="mt-2 text-xs text-slate-500">
                        tokens {formatNumber(lane.totalTokens)} - process{' '}
                        {lane.processAlive === false ? 'dead' : lane.processAlive ? 'alive' : 'n/a'}
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        lane window{' '}
                        <strong className="text-slate-800">
                          {formatDurationHours(lane.elapsedHours)}
                        </strong>{' '}
                        / {formatDurationHours(lane.targetRuntimeHours)}
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        active Codex{' '}
                        <strong className="text-slate-800">
                          {formatDurationHours(lane.activeCodexElapsedHours)}
                        </strong>{' '}
                        · platform {formatDurationHours(lane.platformElapsedHours)}
                      </div>
                      {lane.runtimeWindowStatus ? (
                        <div
                          className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statusTone(
                            lane.runtimeWindowStatus === 'target_reached'
                              ? 'approved'
                              : lane.runtimeWindowStatus === 'running'
                                ? 'running'
                                : lane.runtimeWindowStatus === 'below_target'
                                  ? 'failed'
                                  : 'planned',
                          )}`}
                        >
                          duration {statusLabel(lane.runtimeWindowStatus)}
                        </div>
                      ) : null}
                      {lane.longRunTelemetry ? (
                        <div className="mt-2 grid grid-cols-2 gap-1 rounded-lg border border-slate-200 bg-slate-50 p-2 text-[11px] text-slate-600">
                          <div>
                            useful{' '}
                            <strong className="text-slate-800">
                              {formatNumber(lane.longRunTelemetry.usefulSegmentCount)}
                            </strong>
                          </div>
                          <div>
                            wrappers{' '}
                            <strong className="text-slate-800">
                              {formatNumber(lane.longRunTelemetry.wrapperOnlySegmentCount)}
                            </strong>
                          </div>
                          <div>
                            occupancy{' '}
                            <strong className="text-slate-800">
                              {formatNumber(lane.longRunTelemetry.codexOccupancyRatio)}
                            </strong>
                          </div>
                          <div>
                            gap{' '}
                            <strong className="text-slate-800">
                              {formatNumber(lane.longRunTelemetry.acceptanceGapRemaining)}
                            </strong>
                          </div>
                          {lane.longRunTelemetry.currentStopClassifier ? (
                            <div className="col-span-2">
                              classifier{' '}
                              <strong className="text-slate-800">
                                {lane.longRunTelemetry.currentStopClassifier}
                              </strong>
                            </div>
                          ) : null}
                          {lane.longRunTelemetry.lastUsefulDeltaReasons?.length ? (
                            <div className="col-span-2">
                              delta{' '}
                              {lane.longRunTelemetry.lastUsefulDeltaReasons.slice(0, 3).join(', ')}
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                      {lane.stopReason ? (
                        <div className="mt-1 text-xs text-amber-700">stop: {lane.stopReason}</div>
                      ) : null}
                      {lane.codexExecutionProfile ? (
                        <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">
                          <div className="font-semibold text-slate-800">
                            {lane.codexExecutionProfile.modelDisplayName ??
                              lane.codexExecutionProfile.model}
                          </div>
                          <div>
                            reasoning {lane.codexExecutionProfile.reasoningEffort ?? 'n/a'} - fast{' '}
                            {formatFastMode(lane.codexExecutionProfile.fastMode)}
                          </div>
                        </div>
                      ) : null}
                      {lane.failedGateIds?.length ? (
                        <div className="mt-1 text-xs text-red-700">
                          failed gates: {lane.failedGateIds.join(', ')}
                        </div>
                      ) : null}
                      {lane.blockers?.length ? (
                        <div className="mt-1 text-xs text-red-700">
                          blockers: {lane.blockers.slice(0, 2).join(', ')}
                        </div>
                      ) : null}
                    </div>
                  ))}
                  {!selectedTest.lanes.length ? (
                    <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-600">
                      No lane telemetry yet for this test.
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Visual evidence
                  </div>
                  <h5 className="text-base font-semibold text-slate-950">
                    Screenshots by lane and stage
                  </h5>
                  <p className="mt-1 text-xs leading-5 text-slate-500">
                    Test2D cannot close without timestamped, hashed screenshot refs for scaffold,
                    data/RBAC, workflow, forms, mutations, audit/KPI, mobile and final states.
                  </p>
                </div>
                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                  {selectedVisualEvidence.length} evidence refs
                </span>
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                {selectedVisualEvidence.slice(0, 16).map((item) => (
                  <div
                    key={item.id}
                    className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50"
                  >
                    <div className="flex h-28 items-center justify-center border-b border-slate-200 bg-white">
                      {item.thumbnailDataUrl ? (
                        <img
                          src={item.thumbnailDataUrl}
                          alt={`${item.stage} screenshot`}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="px-4 text-center text-xs font-semibold uppercase tracking-wide text-slate-400">
                          Screenshot ref
                        </div>
                      )}
                    </div>
                    <div className="space-y-1 p-3 text-xs">
                      <div className="flex items-center justify-between gap-2">
                        <strong className="text-slate-950">{item.stage}</strong>
                        <span
                          className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${statusTone(item.status)}`}
                        >
                          {statusLabel(item.status)}
                        </span>
                      </div>
                      <div className="text-slate-500">{item.laneId ?? item.testId}</div>
                      <div className="text-slate-500">
                        {formatViewport(item.viewport)} - {item.route ?? 'route n/a'}
                      </div>
                      <div className="truncate font-mono text-[10px] text-slate-400">
                        {item.hash ?? item.evidenceRef ?? 'hash pending'}
                      </div>
                    </div>
                  </div>
                ))}
                {!selectedVisualEvidence.length ? (
                  <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
                    No screenshot evidence has been attached to this test yet.
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </Card>

      <div className="grid gap-5 xl:grid-cols-[1.2fr_0.8fr]">
        <Card className="border-slate-200 bg-white shadow-sm" bodyClassName="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                A75 lane grid
              </div>
              <h3 className="text-lg font-semibold text-slate-950">Heartbeats and checkpoints</h3>
            </div>
            <span className="text-xs font-semibold text-slate-500">
              {visibleA75Lanes.length} lanes visible
            </span>
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            {visibleA75Lanes.map((lane) => (
              <div key={lane.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center justify-between gap-2">
                  <strong className="text-sm text-slate-900">{lane.label}</strong>
                  <span
                    className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statusTone(lane.status)}`}
                  >
                    {lane.processAlive === false ? 'dead' : lane.status}
                  </span>
                </div>
                <div className="mt-2 text-xs text-slate-500">
                  heartbeat {lane.heartbeatCount} - checkpoint {lane.checkpointCount}
                </div>
              </div>
            ))}
            {!visibleA75Lanes.length ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                No lane telemetry yet.
              </div>
            ) : null}
          </div>
        </Card>

        <Card className="border-slate-200 bg-white shadow-sm" bodyClassName="space-y-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Governance
            </div>
            <h3 className="text-lg font-semibold text-slate-950">Locks, replay and evidence</h3>
          </div>
          <div className="space-y-2">
            {telemetry?.actionLocks.locks.map((lock) => (
              <div
                key={lock.action}
                className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm"
              >
                <div className="flex items-center justify-between gap-2">
                  <strong>{lock.action}</strong>
                  <span className={lock.allowed ? 'text-emerald-700' : 'text-red-700'}>
                    {lock.allowed ? 'allowed' : 'locked'}
                  </span>
                </div>
                <div className="mt-1 text-xs text-slate-500">{lock.reason}</div>
              </div>
            ))}
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            Replay: {telemetry?.replayProof.accepted ? 'accepted' : 'blocked'} - Hash{' '}
            {telemetry?.replayProof.hash?.slice(0, 10) ?? 'n/a'}
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            {telemetry?.report.claimBoundary ?? 'No claim boundary loaded.'}
          </div>
        </Card>
      </div>
    </section>
  );
}

export function RuntimeCockpitScreen(): JSX.Element {
  const [telemetry, setTelemetry] = useState<RuntimeCockpitTelemetryPayload | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const projectId = useMemo(() => {
    if (typeof window === 'undefined') return 'longwalls-platform';
    return (
      new URLSearchParams(window.location.search).get('projectId')?.trim() || 'longwalls-platform'
    );
  }, []);

  const refresh = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const payload = await fetchRuntimeCockpitTelemetry(projectId);
      setTelemetry(payload);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Failed to load runtime cockpit telemetry.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, [projectId]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refresh();
    }, 30_000);
    return () => window.clearInterval(timer);
  }, [projectId]);

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    window.addEventListener('focus', refreshWhenVisible);
    window.addEventListener('pageshow', refreshWhenVisible);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      window.removeEventListener('focus', refreshWhenVisible);
      window.removeEventListener('pageshow', refreshWhenVisible);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [projectId]);

  return (
    <div className="mx-auto max-w-7xl space-y-5 px-4 py-8 sm:px-6 lg:px-8">
      <RuntimeCockpitPanel
        telemetry={telemetry}
        isLoading={isLoading}
        error={error}
        onRefresh={() => void refresh()}
      />
    </div>
  );
}
